import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ResponsiveLine } from "@nivo/line";
import { api } from "../api/client";
import { commsTypeLabel, VERIFIED_MILESTONE_TYPE } from "../lib/commsTypeLabels";

// The Comms page (spec-12): email deliverability & engagement for the platform, with
// a searchable per-message log, a Problems worklist, and the forward schedule absorbed
// from the old SchedulePage. Read-only throughout (no send/pause/cancel) — it reads the
// operator-gated /api/comms/* surface. Four zones behind tabs + a global time window.

type Window = 7 | 30 | 90;
const WINDOWS: Window[] = [7, 30, 90];
type Tab = "overview" | "effectiveness" | "messages" | "problems" | "scheduled";
const TABS: { key: Tab; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "effectiveness", label: "Effectiveness" },
  { key: "messages", label: "Messages" },
  { key: "problems", label: "Problems" },
  { key: "scheduled", label: "Scheduled" },
];

export function CommsPage() {
  const [window, setWindow] = useState<Window>(7);
  const [tab, setTab] = useState<Tab>("overview");

  return (
    <div className="mx-auto max-w-5xl px-8 py-8" data-testid="comms-page">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl text-heading">Comms</h1>
          <p className="text-sm text-muted">Email deliverability, engagement & the forward schedule — read-only.</p>
        </div>
        <div role="group" aria-label="Time window" className="inline-flex items-center rounded-lg border border-edge bg-page p-0.5">
          {WINDOWS.map((w) => (
            <button
              key={w}
              data-testid={`window-${w}`}
              aria-pressed={window === w}
              onClick={() => setWindow(w)}
              className={[
                "rounded-md px-2.5 py-1 text-xs transition-colors",
                window === w ? "bg-card-hover text-heading shadow-sm" : "text-secondary hover:text-primary",
              ].join(" ")}
            >
              {w}d
            </button>
          ))}
        </div>
      </header>

      <nav className="mb-6 flex gap-1 border-b border-edge" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            data-testid={`tab-${t.key}`}
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={[
              "-mb-px border-b-2 px-3 py-2 text-sm transition-colors",
              tab === t.key ? "border-primary text-heading" : "border-transparent text-muted hover:text-primary",
            ].join(" ")}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === "overview" && <OverviewTab window={window} />}
      {tab === "effectiveness" && <EffectivenessTab window={window} />}
      {tab === "messages" && <MessagesTab window={window} />}
      {tab === "problems" && <ProblemsTab window={window} />}
      {tab === "scheduled" && <ScheduledTab />}
    </div>
  );
}

// ── Zone 1: Overview — KPI cards (live Postmark rates) + volume trend + by-type ──

interface Kpis {
  available: boolean;
  unavailableReason?: string;
  sent: number;
  delivered: number;
  bounced: number;
  spamComplaints: number;
  uniqueOpens: number;
  uniqueClicks: number;
  deliveryRate: number;
  bounceRate: number;
  openRate: number;
  clickRate: number;
  spamRate: number;
}
interface Volume {
  byDay: { date: string; count: number }[];
  byType: { type: string; count: number }[];
}

function OverviewTab({ window }: { window: Window }) {
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [volume, setVolume] = useState<Volume | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "error">("loading");

  useEffect(() => {
    setState("loading");
    Promise.all([
      api<{ kpis: Kpis }>(`/api/comms/stats?window=${window}`),
      api<{ volume: Volume }>(`/api/comms/volume?window=${window}`),
    ])
      .then(([k, v]) => {
        setKpis(k.kpis);
        setVolume(v.volume);
        setState("ok");
      })
      .catch(() => setState("error"));
  }, [window]);

  if (state === "loading") return <p className="text-muted">Loading…</p>;
  if (state === "error") return <p className="text-status-danger-text">Failed to load the dashboard.</p>;
  if (!kpis) return null;

  // Health colouring (narrative §2): bounce > 2% or any spam complaint → danger.
  const bounceDanger = kpis.bounceRate > 2;
  const spamDanger = kpis.spamComplaints > 0 || kpis.spamRate > 0;

  // "First verified-AC milestone" signal (spec-16 dec-2): a projection of the existing
  // volume payload — the count of idempotent "See it verified" sends over the window.
  // NOT sourced from users.first_ac_verified_at (a backfill-polluted gate sentinel,
  // off-limits as analytics per spec-453 t-1); we add no query, we read what's already here.
  const milestoneCount = volume?.byType.find((t) => t.type === VERIFIED_MILESTONE_TYPE)?.count ?? 0;

  return (
    <div className="space-y-6">
      {!kpis.available && (
        <p className="rounded-lg border border-edge bg-surface px-4 py-3 text-sm text-muted" data-testid="kpi-unavailable">
          Live deliverability stats are unavailable{kpis.unavailableReason ? ` — ${kpis.unavailableReason}` : ""}.
        </p>
      )}

      <div data-testid="kpi-cards" className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Kpi testid="kpi-sent" label="Sent" value={kpis.sent} />
        <Kpi testid="kpi-delivered" label="Delivered" value={kpis.delivered} sub={`${kpis.deliveryRate}%`} />
        <Kpi testid="kpi-bounced" label="Bounced" value={kpis.bounced} sub={`${kpis.bounceRate}%`} danger={bounceDanger} />
        <Kpi testid="kpi-spam" label="Spam" value={kpis.spamComplaints} sub={`${kpis.spamRate}%`} danger={spamDanger} />
        <Kpi testid="kpi-opens" label="Unique opens" value={kpis.uniqueOpens} sub={`${kpis.openRate}%`} />
        <Kpi testid="kpi-clicks" label="Unique clicks" value={kpis.uniqueClicks} sub={`${kpis.clickRate}%`} />
      </div>

      <section className="rounded-xl border border-edge bg-surface p-4" data-testid="milestone-stat">
        <h2 className="mb-1 text-sm font-medium uppercase tracking-wider text-muted">First verified-AC milestones</h2>
        <div className="text-3xl font-semibold text-heading" data-testid="milestone-count">{milestoneCount}</div>
        <p className="mt-0.5 text-xs text-muted">users who reached their first CI-verified acceptance criterion</p>
        <p className="mt-2 text-xs text-muted" data-testid="milestone-note">
          Counts &ldquo;See it verified&rdquo; sends; excludes users who hit the milestone while{" "}
          <strong>unsubscribed</strong> or while the activation sequence was <strong>paused</strong> (flag off), so it
          slightly under-counts.
        </p>
      </section>

      <section className="rounded-xl border border-edge bg-surface p-4">
        <h2 className="mb-3 text-sm font-medium uppercase tracking-wider text-muted">Send volume</h2>
        <div className="h-56" data-testid="comms-trend" role="img" aria-label="Send volume over time">
          {volume && volume.byDay.length > 0 ? (
            <ResponsiveLine
              data={[{ id: "Sent", data: volume.byDay.map((p) => ({ x: p.date, y: p.count })) }]}
              xScale={{ type: "point" }}
              yScale={{ type: "linear", min: 0, max: "auto" }}
              margin={{ top: 12, right: 16, bottom: 36, left: 40 }}
              curve="monotoneX"
              enablePoints={false}
              enableArea
              areaOpacity={0.12}
              lineWidth={2.5}
              axisBottom={{ tickSize: 0, tickPadding: 8 }}
              axisLeft={{ tickSize: 0, tickPadding: 8 }}
              enableGridX={false}
              animate
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted">No sends in this window.</div>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-edge bg-surface p-4" data-testid="by-type">
        <h2 className="mb-3 text-sm font-medium uppercase tracking-wider text-muted">By email type</h2>
        {volume && volume.byType.length > 0 ? (
          <ul className="space-y-1">
            {volume.byType.map((t) => (
              <li key={t.type} data-testid="by-type-row" className="flex items-center justify-between text-sm">
                <span className="text-primary">{commsTypeLabel(t.type)}</span>
                <span className="text-muted">{t.count}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted">No sends in this window.</p>
        )}
      </section>
    </div>
  );
}

function Kpi({ testid, label, value, sub, danger }: { testid: string; label: string; value: number; sub?: string; danger?: boolean }) {
  return (
    <div className="rounded-xl border border-edge bg-surface p-3" data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-muted">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-heading">{value}</div>
      {sub && <div className={`text-xs ${danger ? "text-status-danger-text" : "text-muted"}`}>{sub}</div>}
    </div>
  );
}

// ── Zone 1b: Effectiveness — activation-drip send→conversion (spec-15) ──
// Not "did the email land" (that's Overview) but "did it work": did the win-back
// recipient take the product action the email was sent to cause. Per-cohort
// sent/converted/rate, with in-flight sends (success window not yet closed) shown
// separately and excluded from the rate (dec-3), plus the combined-v1 note (dec-4).

interface CohortConversion {
  cohort: "connected_inactive" | "signed_in_dormant";
  sent: number;
  converted: number;
  inFlight: number;
  rate: number;
}
const COHORT_LABEL: Record<string, string> = {
  connected_inactive: "Email 1 · Connected-inactive",
  signed_in_dormant: "Email 2 · Signed-in-dormant",
};
const COHORT_GOAL: Record<string, string> = {
  connected_inactive: "Success: first tool call within 24h of send",
  signed_in_dormant: "Success: connected + a non-demo spec created within 48h",
};

function EffectivenessTab({ window }: { window: Window }) {
  const [rows, setRows] = useState<CohortConversion[] | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "error">("loading");

  useEffect(() => {
    setState("loading");
    api<{ window: Window; conversion: CohortConversion[] }>(`/api/comms/conversion?window=${window}`)
      .then((r) => {
        setRows(r.conversion);
        setState("ok");
      })
      .catch(() => setState("error"));
  }, [window]);

  if (state === "loading") return <p className="text-muted">Loading…</p>;
  if (state === "error") return <p className="text-status-danger-text">Failed to load conversion.</p>;
  if (!rows) return null;

  return (
    <div className="space-y-6">
      <div data-testid="conversion-cards" className="grid gap-3 sm:grid-cols-2">
        {rows.map((r) => {
          const denom = r.sent - r.inFlight;
          return (
            <section key={r.cohort} data-testid={`cohort-${r.cohort}`} className="rounded-xl border border-edge bg-surface p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h2 className="text-sm font-medium text-heading">{COHORT_LABEL[r.cohort] ?? r.cohort}</h2>
                  <p className="text-xs text-muted">{COHORT_GOAL[r.cohort] ?? ""}</p>
                </div>
                {r.inFlight > 0 && (
                  <span
                    data-testid="inflight-badge"
                    title="Sends whose success window has not closed yet — excluded from the rate"
                    className="flex-none rounded bg-btn-secondary px-1.5 py-0.5 text-xs text-muted"
                  >
                    {r.inFlight} in-flight
                  </span>
                )}
              </div>
              <div className="mt-3 flex items-end gap-4">
                <div>
                  <div className="text-3xl font-semibold text-heading">{Math.round(r.rate * 100)}%</div>
                  <div className="text-xs text-muted">conversion</div>
                </div>
                <div className="text-xs text-muted">
                  <div>{r.converted} converted</div>
                  <div>of {denom} elapsed{r.inFlight > 0 ? ` · ${r.sent} sent` : ""}</div>
                </div>
              </div>
            </section>
          );
        })}
      </div>
      <p data-testid="combined-note" className="rounded-lg border border-edge bg-surface px-4 py-3 text-xs text-muted">
        Conversion is reported <strong>combined</strong> across the one-time backlog batch and the evergreen daily
        drip — <code>comms_log</code> carries no marker to separate the two sources. The rate counts only sends whose
        success window (24h / 48h) has fully elapsed; in-flight sends are shown separately and excluded from the
        denominator.
      </p>
    </div>
  );
}

// ── Zone 2: Messages — searchable/filterable log + single-message detail drill-down ──

interface MessageRow {
  id: string;
  sourceRef: string | null;
  userId: string;
  recipient: string;
  org: string | null;
  type: string;
  subject: string | null;
  sentAt: string | null;
  createdAt: string;
  outcome: string;
  bounceReason: string | null;
  repeatCount: number;
}

const FAILURE_OUTCOMES = new Set(["Hard bounce", "Transient", "Bounced", "Spam complaint"]);
function outcomeClass(outcome: string): string {
  if (FAILURE_OUTCOMES.has(outcome)) return "text-status-danger-text";
  if (outcome === "Pending") return "text-muted";
  return "text-secondary";
}

function MessagesTab({ window }: { window: Window }) {
  const [search, setSearch] = useState("");
  const [failuresOnly, setFailuresOnly] = useState(false);
  const [repeatsOnly, setRepeatsOnly] = useState(false);
  const [rows, setRows] = useState<MessageRow[]>([]);
  const [total, setTotal] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [state, setState] = useState<"loading" | "ok" | "error">("loading");
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    setState("loading");
    const params = new URLSearchParams({ days: String(window) });
    if (search) params.set("q", search);
    if (failuresOnly) params.set("failures", "1");
    if (repeatsOnly) params.set("repeats", "1");
    api<{ messages: MessageRow[]; total: number; truncated: boolean }>(`/api/comms/messages?${params}`)
      .then((r) => {
        setRows(r.messages);
        setTotal(r.total);
        setTruncated(r.truncated);
        setState("ok");
      })
      .catch(() => setState("error"));
  }, [window, search, failuresOnly, repeatsOnly]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          data-testid="message-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search recipient or subject…"
          className="flex-1 rounded-lg border border-edge bg-surface px-3 py-1.5 text-sm text-primary"
        />
        <label className="flex items-center gap-1.5 text-xs text-muted">
          <input type="checkbox" data-testid="filter-failures" checked={failuresOnly} onChange={(e) => setFailuresOnly(e.target.checked)} />
          Failures only
        </label>
        <label className="flex items-center gap-1.5 text-xs text-muted">
          <input type="checkbox" data-testid="filter-repeats" checked={repeatsOnly} onChange={(e) => setRepeatsOnly(e.target.checked)} />
          Repeat sends only
        </label>
      </div>

      {state === "loading" && <p className="text-muted">Loading…</p>}
      {state === "error" && <p className="text-status-danger-text">Failed to load the message log.</p>}
      {state === "ok" && rows.length === 0 && (
        <p className="text-muted" data-testid="messages-empty">No messages match these filters.</p>
      )}

      {state === "ok" && rows.length > 0 && (
        <>
          <p className="mb-2 text-xs text-muted" data-testid="messages-count">
            Showing {rows.length} of {total}
            {truncated ? " — narrow the filters for more" : ""}
          </p>
          <table data-testid="messages-table" className="w-full overflow-hidden rounded-xl border border-edge text-sm">
            <thead className="bg-page text-left text-xs uppercase tracking-wider text-muted">
              <tr>
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">Recipient</th>
                <th className="px-3 py-2">Subject</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr
                  key={m.id}
                  data-testid="message-row"
                  onClick={() => setOpenId(m.id)}
                  className="cursor-pointer border-t border-edge-subtle hover:bg-card-hover"
                >
                  <td className="px-3 py-2 text-muted">{new Date(m.sentAt ?? m.createdAt).toLocaleString()}</td>
                  <td className="px-3 py-2">
                    <Link to={`/users/${m.userId}`} onClick={(e) => e.stopPropagation()} className="text-primary hover:underline">
                      {m.recipient}
                    </Link>
                    {m.org && <span className="ml-1 text-xs text-muted">· {m.org}</span>}
                  </td>
                  <td className="px-3 py-2 text-secondary">
                    {m.subject ?? "—"}
                    {m.repeatCount > 1 && (
                      <span data-testid="repeat-badge" className="ml-1 rounded bg-btn-secondary px-1 text-xs text-muted">×{m.repeatCount}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted">{commsTypeLabel(m.type)}</td>
                  <td className={`px-3 py-2 ${outcomeClass(m.outcome)}`} data-testid="message-outcome">{m.outcome}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {openId && <MessageDetailDrawer id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

interface Detail {
  recipient: string;
  userId: string;
  subject: string | null;
  type: string;
  source: "postmark" | "local";
  liveDetailUnavailable: boolean;
  unavailableReason?: string;
  events: { type: string; occurredAt: string | null; detail: string | null }[];
}

function MessageDetailDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const [detail, setDetail] = useState<Detail | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "error">("loading");

  useEffect(() => {
    setState("loading");
    api<{ detail: Detail }>(`/api/comms/messages/${id}`)
      .then((r) => {
        setDetail(r.detail);
        setState("ok");
      })
      .catch(() => setState("error"));
  }, [id]);

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/30" onClick={onClose} data-testid="message-detail">
      <aside className="h-full w-full max-w-md overflow-y-auto bg-surface p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between">
          <h2 className="text-lg text-heading">Message detail</h2>
          <button onClick={onClose} data-testid="detail-close" className="text-muted hover:text-primary">✕</button>
        </div>
        {state === "loading" && <p className="text-muted">Loading…</p>}
        {state === "error" && <p className="text-status-danger-text">Failed to load this message.</p>}
        {state === "ok" && detail && (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-primary">{detail.subject ?? "(no subject)"}</p>
              <p className="text-xs text-muted">
                <Link to={`/users/${detail.userId}`} className="hover:underline">{detail.recipient}</Link> · {commsTypeLabel(detail.type)}
              </p>
            </div>
            {detail.liveDetailUnavailable && (
              <p className="rounded border border-edge px-2 py-1 text-xs text-muted" data-testid="detail-unavailable">
                Live detail unavailable{detail.unavailableReason ? ` — ${detail.unavailableReason}` : ""}. Showing local events.
              </p>
            )}
            <ol className="space-y-2" data-testid="detail-events">
              {detail.events.length === 0 && <li className="text-sm text-muted">No events recorded.</li>}
              {detail.events.map((e, i) => (
                <li key={i} className="flex items-start justify-between gap-3 border-b border-edge-subtle pb-2 text-sm">
                  <span className="text-primary">{e.type}</span>
                  <span className="flex-none text-xs text-muted">{e.occurredAt ? new Date(e.occurredAt).toLocaleString() : "—"}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </aside>
    </div>
  );
}

// ── Zone 3: Problems worklist ──

interface Problems {
  stuckSignups: { userId: string; email: string; name: string | null; bounceReason: string | null; recoverability: string; isInternal: boolean }[];
  suppressions: { email: string; reason: string | null; suppressedAt: string | null; isInternal: boolean; matchesRealAccount: boolean; userId: string | null }[];
  suppressionsAvailable: boolean;
  highRetry: { userId: string; email: string; sendCount: number; isInternal: boolean }[];
}

function ProblemsTab({ window }: { window: Window }) {
  const [data, setData] = useState<Problems | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "error">("loading");

  useEffect(() => {
    setState("loading");
    api<Problems>(`/api/comms/problems?days=${window}`)
      .then((r) => {
        setData(r);
        setState("ok");
      })
      .catch(() => setState("error"));
  }, [window]);

  if (state === "loading") return <p className="text-muted">Loading…</p>;
  if (state === "error") return <p className="text-status-danger-text">Failed to load the worklist.</p>;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <section data-testid="stuck-signups">
        <h2 className="mb-2 text-sm font-medium uppercase tracking-wider text-muted">Stuck signups ({data.stuckSignups.length})</h2>
        {data.stuckSignups.length === 0 ? (
          <p className="text-muted">No stuck signups — every recent confirmation landed.</p>
        ) : (
          <ul className="overflow-hidden rounded-xl border border-edge">
            {data.stuckSignups.map((s) => (
              <li key={s.userId} data-testid="stuck-row" className="border-b border-edge-subtle px-4 py-3 last:border-0">
                <div className="flex items-center gap-2">
                  <Link to={`/users/${s.userId}`} className="text-sm font-medium text-primary hover:underline">{s.email}</Link>
                  {s.isInternal && <span className="rounded bg-btn-secondary px-1 text-xs text-muted">internal</span>}
                </div>
                <p className="mt-0.5 text-xs text-status-danger-text">{s.bounceReason ?? "Confirmation undeliverable"}</p>
                <p className="text-xs text-muted">{s.recoverability}</p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section data-testid="suppression-list">
        <h2 className="mb-2 text-sm font-medium uppercase tracking-wider text-muted">Suppressions ({data.suppressions.length})</h2>
        {!data.suppressionsAvailable ? (
          <p className="text-muted" data-testid="suppressions-unavailable">Live suppression list unavailable (Postmark not configured).</p>
        ) : data.suppressions.length === 0 ? (
          <p className="text-muted">No suppressions in this stream.</p>
        ) : (
          <ul className="overflow-hidden rounded-xl border border-edge">
            {data.suppressions.map((s) => (
              <li key={s.email} data-testid="suppression-row" className="flex items-center justify-between gap-3 border-b border-edge-subtle px-4 py-2 last:border-0 text-sm">
                <span className="flex items-center gap-2">
                  {s.matchesRealAccount && s.userId ? (
                    <Link to={`/users/${s.userId}`} className="text-primary hover:underline">{s.email}</Link>
                  ) : (
                    <span className="text-primary">{s.email}</span>
                  )}
                  {s.isInternal && <span className="rounded bg-btn-secondary px-1 text-xs text-muted">internal</span>}
                  {s.matchesRealAccount && <span data-testid="matches-real" className="rounded bg-status-danger-bg px-1 text-xs text-status-danger-text">real account</span>}
                </span>
                <span className="flex-none text-xs text-muted">{s.reason ?? ""}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section data-testid="high-retry">
        <h2 className="mb-2 text-sm font-medium uppercase tracking-wider text-muted">High-retry recipients ({data.highRetry.length})</h2>
        {data.highRetry.length === 0 ? (
          <p className="text-muted">No recipient is receiving an unusual number of sends.</p>
        ) : (
          <ul className="overflow-hidden rounded-xl border border-edge">
            {data.highRetry.map((r) => (
              <li key={r.userId} data-testid="high-retry-row" className="flex items-center justify-between border-b border-edge-subtle px-4 py-2 last:border-0 text-sm">
                <Link to={`/users/${r.userId}`} className="text-primary hover:underline">{r.email}</Link>
                <span className="text-muted">{r.sendCount} sends</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ── Zone 4: Scheduled — the forward schedule (absorbed from SchedulePage) ──

interface ScheduledEntry {
  id: string;
  channel: string;
  type: string;
  scheduledFor: string | null;
  subject: string | null;
  userId: string;
  userEmail: string;
  userName: string | null;
}
const CHANNELS = ["", "email", "in_app", "badge", "os"] as const;
const CHANNEL_LABEL: Record<string, string> = { "": "All channels", email: "Email", in_app: "In-app", badge: "Badge", os: "OS" };

function ScheduledTab() {
  const [channel, setChannel] = useState("");
  const [entries, setEntries] = useState<ScheduledEntry[] | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "error">("loading");

  const load = useCallback(() => {
    setState("loading");
    const qs = channel ? `?channel=${encodeURIComponent(channel)}` : "";
    api<{ schedule: ScheduledEntry[] }>(`/api/comms/schedule${qs}`)
      .then((r) => {
        setEntries(r.schedule);
        setState("ok");
      })
      .catch(() => setState("error"));
  }, [channel]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <select
          data-testid="channel-filter"
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
          className="rounded-lg border border-edge bg-surface px-3 py-1.5 text-sm text-primary"
        >
          {CHANNELS.map((c) => (
            <option key={c} value={c}>{CHANNEL_LABEL[c]}</option>
          ))}
        </select>
      </div>

      {state === "loading" && <p className="text-muted">Loading…</p>}
      {state === "error" && <p className="text-status-danger-text">Failed to load the schedule.</p>}
      {state === "ok" && entries && entries.length === 0 && (
        <p className="text-muted" data-testid="schedule-empty">Nothing scheduled.</p>
      )}
      {state === "ok" && entries && entries.length > 0 && (
        <ul data-testid="schedule-list" className="overflow-hidden rounded-xl border border-edge">
          {entries.map((e) => (
            <li key={e.id} data-testid="schedule-entry" className="flex items-center justify-between gap-4 border-b border-edge-subtle px-4 py-3 last:border-0">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="rounded-md bg-btn-secondary px-1.5 py-0.5 text-xs text-secondary">{CHANNEL_LABEL[e.channel] ?? e.channel}</span>
                  <span className="truncate text-sm font-medium text-primary">{e.subject ?? commsTypeLabel(e.type)}</span>
                </div>
                <p className="mt-0.5 text-xs text-muted">
                  <Link to={`/users/${e.userId}`} className="text-primary hover:underline">{e.userName ?? e.userEmail}</Link> · {commsTypeLabel(e.type)}
                </p>
              </div>
              <span className="flex-none text-xs text-muted">{e.scheduledFor ? new Date(e.scheduledFor).toLocaleString() : "—"}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
